import { useCallback, useLayoutEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { Resume, StepByStepFlowStepId } from 'packages/types'

import AnchorManager from 'builder/services/AnchorManager'
import FocusManager from 'builder/services/FocusManager'

import { useFeaturesConfig } from 'builder/hooks/featureConfig/useFeaturesConfig'
import { getScoreSuggestions } from 'builder/modules/resumeEditor/resumeScoreOracle'
import { getEmptyCard } from 'builder/modules/resumeEditor/resumeScoreRules/sectionRule'
import { actions, ResumeFieldSuggest, SectionNames } from 'builder/modules/resumeEditor'

import { generateRandomId } from 'builder/utils/generateRandomId'
import { useStepByStepFeature } from '../StepByStepFlow/hooks/useStepByStepFeature'

export const useScrollTo = (resume: Resume | null) => {
  const { hash } = useLocation()
  const dispatch = useDispatch()
  const features = useFeaturesConfig()
  const { isRbpStepFlowActive } = useStepByStepFeature()

  const getSuggestion = useCallback((): ResumeFieldSuggest | undefined => {
    const { suggestions } = getScoreSuggestions(resume as Resume)
    return suggestions?.find(suggestion => `#${suggestion.key}` === hash)
  }, [resume, hash])

  const clearUrlHash = () => {
    window.history.pushState({}, '', `${window.location.origin}${window.location.pathname}`)
  }

  const handleChangeStep = useCallback(
    (sectionId: StepByStepFlowStepId) => {
      dispatch(actions.setStepByStepFlowState({ shouldRedirectToAdditionalStep: true }))
      dispatch(
        actions.updateSimpleField({
          name: 'data',
          value: {
            stepByStepFlow: {
              currentStep: sectionId,
            },
          },
          debounce: true,
        }),
      )
    },
    [dispatch],
  )

  const goToStep = useCallback(() => {
    const suggestion = getSuggestion()
    if (suggestion) {
      setTimeout(() => {
        handleChangeStep(suggestion.sectionName || (suggestion.sectionId as StepByStepFlowStepId))
      }, 10)
    }
    clearUrlHash()
  }, [getSuggestion, handleChangeStep])

  const scrollToField = useCallback(() => {
    const suggestion = getSuggestion()

    if (suggestion?.type === 'simpleField' && suggestion?.fieldId) {
      dispatch(actions.setOpenedSections({ [SectionNames.details]: true }))

      AnchorManager.scrollToAnchor(suggestion.sectionId || suggestion.fieldId)
      FocusManager.focus(suggestion.fieldId)
    }

    clearUrlHash()
  }, [dispatch, getSuggestion])

  const scrollToSection = useCallback(() => {
    const suggestion = getSuggestion()
    const { sectionName, type } = suggestion || {}
    if (type !== 'section' || !sectionName) return

    const emptyCard = resume && getEmptyCard(sectionName, resume)
    if (emptyCard) {
      const cardId = emptyCard.cid || emptyCard.id
      dispatch(actions.openCard({ id: cardId, sectionId: sectionName }))

      setTimeout(() => {
        AnchorManager.scrollToAnchor(cardId)
        FocusManager.focus(cardId)
      }, 500)
    } else {
      dispatch(
        actions.addCard({
          sectionName,
          cardId: generateRandomId(),
          options: { scrollIntoViewport: true, shouldOpen: true },
        }),
      )
    }

    clearUrlHash()
  }, [dispatch, getSuggestion, resume])

  useLayoutEffect(() => {
    if (!features.isDashboardV4() || !hash) return

    if (resume) {
      if (isRbpStepFlowActive) {
        goToStep()
      } else {
        scrollToField()
        scrollToSection()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume, hash, scrollToField, scrollToSection, goToStep, isRbpStepFlowActive])
}
